import {
  shorthands,
  makeStyles,
  mergeClasses,
  useMenuButtonStyles_unstable,
} from "@fluentui/react-components";

// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastBackgroundHoverFocus,
  commonHighContrastColorHoverFocus,
  commonHighContrastOutlineToggledOn,
  HighContrastSelector,
  useCommonDisabledStyles,
} from "@1js/acui-common/lib/styling/next/commonStyles.styles";
import {
  useCommonRibbonButtonStyles,
  getRibbonButtonTypeStyles,
} from "../../next/commonRibbonButtonStyles.styles";
import type { RibbonButtonState } from "../../RibbonButton/next";
import type { RibbonMenuButtonState } from ".";
import { menuTextContainerClass } from "@1js/acui-button";
import { customTokens, isNextStyleEnabled } from "@1js/acui-theme";

const useRibbonMenuButtonTextContainerStyles = makeStyles({
  medium: {
    paddingLeft: "10px",
  },
  small: {
    paddingLeft: "4px",
  },
});

export const useRibbonMenuButtonTextContainerClassNames = (
  size?: RibbonButtonState["size"]
) => {
  const ribbonMenuButtonTextContainerStyles =
    useRibbonMenuButtonTextContainerStyles();
  return mergeClasses(
    size === "small" && ribbonMenuButtonTextContainerStyles.small,
    size === "medium" && ribbonMenuButtonTextContainerStyles.medium
  );
};

const useBaseStyles = makeStyles({
  medium: {
    paddingLeft: "9px",
    paddingRight: "0px",
  },
  small: {
    paddingTop: "1px",
    paddingLeft: "0px",
    paddingRight: "0px",
    paddingBottom: "1px",
  },
  large: {
    paddingLeft: "0px",
    paddingTop: "1px",
    paddingRight: "0px",
    paddingBottom: "1px",
    [`& .${menuTextContainerClass}`]: {
      whiteSpace: "nowrap",
    },
  },
  expanded: {
    backgroundColor: customTokens.legacy_neutralLight,
    ...shorthands.borderColor("transparent"),
    color: customTokens.legacy_neutralDark,

    [`${HighContrastSelector}`]: {
      ...commonHighContrastBackgroundHoverFocus,
      ...commonHighContrastColorHoverFocus,
      ...commonHighContrastOutlineToggledOn,
    },
  },
  overflowButton: {
    paddingRight: "9px",
  },
});

const useBaseStylesNext = makeStyles({
  small: {
    paddingRight: "1px",
  },
  medium: {
    paddingRight: "3px",
  },
  overflowButton: {
    ...shorthands.padding("5px"),
  },
  expanded: {
    backgroundColor: customTokens.colorNeutralBackground1Selected,
  },
});

const useIconStyles = makeStyles({
  small: {
    marginLeft: "1px",
  },
  medium: {
    marginRight: "0px",
    paddingLeft: "0px",
    marginTop: "2px",
  },
  large: {
    marginTop: "1px",
  },
  ribbonModeButton: {
    marginTop: "0px",
    fill: customTokens.legacy_neutralDark,
  },
});

const useIconStylesNext = makeStyles({
  ribbonModeButton: {
    fill: customTokens.colorNeutralForeground1,
  },
});

const useMenuIconStyles = makeStyles({
  medium: {
    fontSize: "12px",
    height: "12px",
    lineHeight: "12px",
    marginLeft: "4px",
    marginRight: "2px",
    width: "12px",
    fill: customTokens.legacy_neutralDark,
  },
  small: {
    height: "8px",
    fontSize: "8px",
    lineHeight: "8px",
    width: "8px",
    marginRight: "1px",
    marginLeft: "4px",
    fill: customTokens.legacy_neutralDark,
  },
});

const useMenuIconStylesNext = makeStyles({
  base: {
    fill: customTokens.colorNeutralForeground1,
  },
  small: {
    height: "8px",
    width: "8px",
    fontSize: "8px",
    lineHeight: "8px",
    marginLeft: customTokens.spacingHorizontalXS,
  },
  medium: {
    lineHeight: "12px",
    marginLeft: customTokens.spacingHorizontalXS,
  },
});

export const useRibbonMenuButtonStyles = (
  state: RibbonMenuButtonState
): RibbonMenuButtonState => {
  const baseStyles = useBaseStyles();
  const iconStyles = useIconStyles();
  const menuIconStyles = useMenuIconStyles();
  const baseStylesNext = useBaseStylesNext();
  const iconStylesNext = useIconStylesNext();
  const menuIconStylesNext = useMenuIconStylesNext();
  const disabledStyles = useCommonDisabledStyles();

  if (isNextStyleEnabled()) {
    state.root.className = mergeClasses(
      getRibbonButtonTypeStyles(state, baseStylesNext),
      !state.menuHidden && baseStylesNext.expanded,
      state.variant === "overflowButton" && baseStylesNext.overflowButton,
      state.root.className
    );

    if (state.icon) {
      state.icon.className = mergeClasses(
        getRibbonButtonTypeStyles(state, iconStylesNext),
        state.icon.className
      );
    }

    if (state.menuIcon) {
      state.menuIcon.className = mergeClasses(
        menuIconStylesNext.base,
        state.disabled && disabledStyles.base,
        getRibbonButtonTypeStyles(state, menuIconStylesNext),
        state.menuIcon.className
      );
    }
  } else {
    state.root.className = mergeClasses(
      getRibbonButtonTypeStyles(state, baseStyles),
      !state.menuHidden && baseStyles.expanded,
      state.variant === "overflowButton" && baseStyles.overflowButton,
      state.root.className
    );

    if (state.icon) {
      state.icon.className = mergeClasses(
        getRibbonButtonTypeStyles(state, iconStyles),
        state.icon.className
      );
    }

    if (state.menuIcon) {
      state.menuIcon.className = mergeClasses(
        state.disabled && disabledStyles.base,
        getRibbonButtonTypeStyles(state, menuIconStyles),
        state.menuIcon.className
      );
    }
  }

  // Fill in iconPosition because that is the only thing RibbonMenuButtonState doesn't have
  // that RibbonButtonState has
  useCommonRibbonButtonStyles({
    ...state,
    iconPosition: "before",
  } as RibbonButtonState);

  useMenuButtonStyles_unstable(state);

  return state;
};
